import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

function NoticeAreas() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [notice, setNotice] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNoticeAreas();
  }, [noticeId]);

  const fetchNoticeAreas = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/notices/areas/${noticeId}`);
      
      if (response.data.status === 1) {
        const data = response.data.data;
        setNotice(data.row);
        setAreas(data.row.areas || []);
      } else {
        throw new Error(response.data.message || 'Failed to load notice areas');
      }
    } catch (err) {
      console.error('Error fetching notice areas:', err);
      toast.error(err.message || 'Failed to load notice areas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-4 py-6 md:p-8 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: '#0087D4' }}
        ></div>
      </div>
    );
  }

  if (!notice) {
    return (
      <div className="p-4 py-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 shadow-sm">
            <p className="text-yellow-700">Notice not found</p>
            <button
              onClick={() => navigate('/dashboard/notices')}
              className="mt-4 text-center py-2 px-4 rounded-lg text-neutral-600 bg-neutral-200 hover:bg-neutral-300 transition"
            >
              Back to Notices
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 py-6 md:p-8 space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{notice.notice_title}</h2>
              <p className="text-sm text-gray-600 mt-1">{formatDate(notice.notice_time)}</p>
            </div>
            <div className="flex gap-2">
              <Link
                to={`../${noticeId}`}
                className="inline-flex items-center px-4 py-2 text-sm text-white rounded"
                style={{ backgroundColor: '#0087D4' }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View Notice
              </Link>
              <button
                onClick={() => toast.info('Add area functionality coming soon')}
                className="inline-flex items-center px-4 py-2 text-sm text-white rounded"
                style={{ backgroundColor: '#0087D4' }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Affected Area
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Areas Table */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Area</th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Suburb/Township</th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Streets</th>
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {areas.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                      No affected areas added yet. Click "Add Affected Area" to add one.
                    </td>
                  </tr>
                ) : (
                  areas.map((area) => {
                    const streets = area.notice_area_streets ? area.notice_area_streets.split(',') : [];
                    return (
                      <tr key={area.notice_area_id}>
                        <td className="border border-gray-300 px-4 py-2">{area.area_name}</td>
                        <td className="border border-gray-300 px-4 py-2">{area.sub_area_name || '-'}</td>
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {streets.map((street, index) => (
                              street.trim() && (
                                <span
                                  key={index}
                                  className="inline-block px-2 py-1 text-xs text-white rounded"
                                  style={{ backgroundColor: '#0087D4' }}
                                >
                                  {street.trim()}
                                </span>
                              )
                            ))}
                          </div>
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex gap-2">
                            <button
                              onClick={() => toast.info('Edit area functionality coming soon')}
                              className="inline-flex items-center px-2 py-1 text-sm text-white rounded"
                              style={{ backgroundColor: '#0087D4' }}
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            {can('Delete Notice') && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to remove this area?')) {
                                    toast.info('Delete functionality coming soon');
                                  }
                                }}
                                className="inline-flex items-center px-2 py-1 text-sm text-white bg-red-600 rounded hover:bg-red-700"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NoticeAreas;
